/**
 * Veilans scroll reveals — fade-up on enter viewport.
 * Pins homepage to top on load/reload (theme uses .page-wrapper as scroll container).
 * Also animates PLP product cards as they scroll into view (incl. infinite scroll).
 */
import { getScrollContainer, getIntersectionRoot, scrollTo } from '@theme/scroll-container';

const isHome = Boolean(
  window.Theme?.template?.name === 'index' || document.querySelector('.veilans-hero, .veilans-the-edit')
);

const clearSavedScroll = () => {
  if (!isHome || window.location.hash) return;
  try {
    const current = typeof history.state === 'object' && history.state !== null ? { ...history.state } : {};
    if ('scrollTop' in current) {
      delete current.scrollTop;
      history.replaceState(current, '');
    }
  } catch (_) {
    // ignore
  }
};

const pinTop = () => {
  if (!isHome || window.location.hash) return;
  scrollTo({ top: 0, behavior: 'instant' });
  const container = getScrollContainer();
  if (container && container !== document.documentElement && container !== document.body) {
    container.scrollTop = 0;
  }
  window.scrollTo(0, 0);
};

clearSavedScroll();
pinTop();
requestAnimationFrame(() => {
  clearSavedScroll();
  pinTop();
});

window.addEventListener(
  'pageshow',
  () => {
    clearSavedScroll();
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        clearSavedScroll();
        pinTop();
      });
    });
  },
  true
);

window.addEventListener(
  'load',
  () => {
    clearSavedScroll();
    pinTop();
  },
  { once: true }
);

const REDUCED = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
if (REDUCED) {
  // Keep pin behavior above; skip reveals only.
} else {
  const ROOT_SELECTORS = [
    '.veilans-cta-strip__inner',
    '.veilans-the-edit__header-copy',
    '.veilans-the-edit__shop-all',
    '.veilans-edit-card',
    '.veilans-the-edit__collage .veilans-category-tile',
    '.veilans-philosophy__media',
    '.veilans-philosophy__copy-inner',
    '.veilans-details__header',
    '.veilans-details__item',
    '.veilans-campaign__content',
    '.veilans-newsletter__inner',
    '.veilans-collection-hero__inner',
    '.veilans-collection-tabs',
    '.veilans-search-page__inner',
    '.product-grid__item',
    '.veilans-collection-related .veilans-campaign__content',
    '.veilans-pdp__breadcrumbs',
    '.veilans-pdp__media',
    '.veilans-pdp__buy-header',
    '.veilans-pdp .product-details > .group-block > *',
    '.veilans-pdp__trust',
    '.veilans-pdp__acc',
    '.veilans-product-features__intro',
    '.veilans-product-features__card',
    '.veilans-product-related__header',
    '.veilans-product-related .veilans-edit-card',
  ];

  const ioRoot = getIntersectionRoot();
  /** @type {WeakSet<Element>} */
  const seen = new WeakSet();
  /** @type {Map<Element, HTMLElement[]>} */
  const staggerParents = new Map();

  const observer = new IntersectionObserver(
    (entries) => {
      for (const entry of entries) {
        if (!entry.isIntersecting) continue;
        entry.target.classList.add('is-inview');
        observer.unobserve(entry.target);
      }
    },
    {
      root: ioRoot,
      rootMargin: '0px 0px -8% 0px',
      threshold: 0.12,
    }
  );

  /**
   * @param {HTMLElement} el
   * @param {{ forceStagger?: boolean }} [options]
   */
  const prepareReveal = (el, options = {}) => {
    if (seen.has(el)) return;
    if (el.closest('.veilans-hero')) return;
    seen.add(el);

    const rect = el.getBoundingClientRect();
    const onFirstScreen = rect.top < window.innerHeight * 0.9 && rect.bottom > 0;

    if (onFirstScreen && !options.forceStagger) {
      el.classList.add('veilans-reveal', 'is-inview');
      return;
    }

    el.classList.add('veilans-reveal');
    const parent = el.parentElement;
    if (parent) {
      if (!staggerParents.has(parent)) staggerParents.set(parent, []);
      staggerParents.get(parent).push(el);
    }
    observer.observe(el);
  };

  const applyStagger = () => {
    for (const group of staggerParents.values()) {
      if (group.length < 2) continue;
      group.forEach((el, index) => {
        el.style.setProperty('--reveal-delay', `${Math.min(index * 80, 480)}ms`);
      });
    }
    staggerParents.clear();
  };

  /**
   * @param {ParentNode} [root]
   */
  const collectAndPrepare = (root = document) => {
    for (const selector of ROOT_SELECTORS) {
      root.querySelectorAll(selector).forEach((node) => {
        if (!(node instanceof HTMLElement)) return;
        prepareReveal(node);
      });
    }
    applyStagger();
  };

  collectAndPrepare();

  // Infinite scroll / filter refreshes inject new product cards
  const gridRoots = document.querySelectorAll('results-list, .product-grid, .main-collection-grid');
  for (const gridRoot of gridRoots) {
    const mo = new MutationObserver((mutations) => {
      let added = false;
      for (const mutation of mutations) {
        for (const node of mutation.addedNodes) {
          if (!(node instanceof HTMLElement)) continue;
          if (node.matches?.('.product-grid__item')) {
            prepareReveal(node, { forceStagger: true });
            added = true;
          } else {
            node.querySelectorAll?.('.product-grid__item').forEach((item) => {
              if (item instanceof HTMLElement) {
                prepareReveal(item, { forceStagger: true });
                added = true;
              }
            });
          }
        }
      }
      if (added) applyStagger();
    });

    mo.observe(gridRoot, { childList: true, subtree: true });
  }
}
